import { useRef, useEffect, useState } from 'react';
import $ from 'jquery';
import 'datatables.net-dt/js/dataTables.dataTables';
import 'datatables.net-buttons/js/dataTables.buttons';
import 'datatables.net-buttons/js/buttons.html5';
import 'jszip/dist/jszip';
import 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios';
import TehsilMapView from './TehsilMapView';

const ReportDepartmentSummary = ({ departmentData }) => {
  const tableRef = useRef(null);
  const [totals, setTotals] = useState({
    totalUsers: 0,
    totalActivities: 0,
    totalPlants: 0
  });
  const [drillDownData, setDrillDownData] = useState(null);
  const [currentView, setCurrentView] = useState('departments');
  const [currentDepartment, setCurrentDepartment] = useState(null);
  const [currentDistrict, setCurrentDistrict] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tehsilPlantations, setTehsilPlantations] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  const handleTehsilClick = (tehsil) => {
    if (!tehsil.plantations || !tehsil.plantations.length) return;
    setTehsilPlantations(tehsil.plantations);
    setCurrentView('map');
  };

  // Calculate totals
  useEffect(() => {
    if (departmentData && departmentData.length > 0 && currentView === 'departments') {
      const calculatedTotals = departmentData.reduce((acc, dept) => ({
        totalUsers: acc.totalUsers + (dept.totalUsers || 0),
        totalActivities: acc.totalActivities + (dept.totalActivities || 0),
        totalPlants: acc.totalPlants + (dept.totalPlants || 0)
      }), { totalUsers: 0, totalActivities: 0, totalPlants: 0 });
      
      setTotals(calculatedTotals);
    } else {
      setTotals({ totalUsers: 0, totalActivities: 0, totalPlants: 0 });
    }
  }, [departmentData, currentView]);

  // Initialize DataTable
  useEffect(() => {
    if (!departmentData || departmentData.length === 0 || !tableRef.current || currentView !== 'departments') {
      return;
    }

    // Destroy existing DataTable
    if ($.fn.DataTable.isDataTable(tableRef.current)) {
      $(tableRef.current).DataTable().destroy();
    }

    const table = $(tableRef.current).DataTable({
      dom: 'Bfrtip',
      buttons: [
        {
          extend: 'csv',
          text: 'Export CSV',
          className: 'btn-csv-export',
          exportOptions: { columns: ':visible' }
        }
      ],
      destroy: true,
      responsive: true,
      paging: false,
      searching: true,
      info: false,
      autoWidth: false,
      order: [],
      columnDefs: [
        {
          targets: 0,
          data: null,
          render: (data, type, row, meta) => meta.row + 1,
          width: '50px',
          className: 'text-center'
        }
      ],
      footerCallback: function () {
        const api = this.api();
        $(api.column(2).footer()).html(totals.totalUsers);
        $(api.column(3).footer()).html(totals.totalActivities);
        $(api.column(4).footer()).html(totals.totalPlants.toLocaleString());
      }
    });

    // Capture current DOM node and DataTable instance for cleanup
    const currentNode = tableRef.current;
    let dataTableInstance = table;

    // Cleanup function
    return () => {
      if (dataTableInstance && currentNode && $.fn.DataTable.isDataTable(currentNode)) {
        dataTableInstance.destroy();
      }
    };
  }, [departmentData, totals, currentView]);

  const fetchDepartmentStats = async (departmentName, district = null, tehsil = null) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const requestData = { province: "Punjab", name: departmentName };
      if (district) requestData.district = district;
      if (tehsil) requestData.tehsil = tehsil;

      const response = await axios.post(
        `${API_BASE_URL}/api/plantation/pafs/department-summary`,
        requestData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.data.success) {
        setDrillDownData(response.data.data);
        return response.data.data;
      }
    } catch (error) {
      console.error("Error fetching department stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDepartmentClick = async (department) => {
    setCurrentDepartment(department.departmentName);
    const data = await fetchDepartmentStats(department.departmentName);
    if (data) setCurrentView('districts');
  };

  const handleDistrictClick = async (district) => {
    const districtName = district.district || district.tehsil;
    setCurrentDistrict(districtName);
    const data = await fetchDepartmentStats(currentDepartment, districtName);
    if (data) setCurrentView('tehsils');
  };

  const handleBackClick = () => {
    if (currentView === 'tehsils') {
      setCurrentView('districts');
      setCurrentDistrict(null);
    } else if (currentView === 'districts') {
      setCurrentView('departments');
      setCurrentDepartment(null);
      setDrillDownData(null);
    } else if (currentView === 'map') {
      setCurrentView('tehsils');
      setTehsilPlantations(null);
    }
  };

  const exportToCSV = () => {
    if (tableRef.current && $.fn.DataTable.isDataTable(tableRef.current) && currentView === 'departments') {
      $(tableRef.current).DataTable().button('.btn-csv-export').trigger();
    }
  };

  const renderDepartmentsTable = () => (
    <>
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="card-title mb-0">Department Summary</h5>
        <button 
          onClick={exportToCSV} 
          className="btn btn-success btn-sm me-2 d-flex align-items-center"
          disabled={!departmentData || departmentData.length === 0}
        >
          <Icon icon="mdi:file-delimited-outline" className="me-1" style={{ fontSize: '16px' }} /> 
          Download CSV
        </button>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table 
            ref={tableRef} 
            className="table vertical-striped-table mb-0 display"
            style={{ width: '100%' }}
          >
            <thead>
              <tr>
                <th scope="col" style={{ width: '50px', textAlign: 'center' }}>Sr.No.</th>
                <th scope="col">Department Name</th>
                <th scope="col">Total Users</th>
                <th scope="col">Total Activities</th>
                <th scope="col">Total Plants</th>
              </tr>
            </thead>
            <tbody>
              {departmentData && departmentData.length > 0 ? (
                departmentData.map((dept, index) => (
                  <tr key={index}>
                    <td style={{ width: '50px', textAlign: 'center' }}>{index + 1}</td>
                    <td>
                      <h6 
                        className="text-md mb-0 fw-normal cursor-pointer text-primary" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDepartmentClick(dept)}
                      >
                        {dept.departmentName || 'Unknown Department'}
                      </h6>
                    </td>
                    <td>{dept.totalUsers}</td>
                    <td>{dept.totalActivities}</td>
                    <td>{dept.totalPlants.toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-20">
                    <div className="d-flex flex-column align-items-center">
                      <Icon icon="mdi:database-off" className="text-3xl text-muted mb-10" />
                      <p className="text-muted">No department data available</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              {departmentData && departmentData.length > 0 && (
                <tr className="fw-bold">
                  <td style={{ width: '50px' }}></td>
                  <td>TOTAL</td>
                  <td>{totals.totalUsers}</td>
                  <td>{totals.totalActivities}</td>
                  <td>{totals.totalPlants.toLocaleString()}</td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      </div>
    </>
  );

  const renderDistrictsTable = () => (
    <>
      <div className="card-header d-flex justify-content-between align-items-center">
        <button onClick={handleBackClick} className="btn btn-secondary btn-sm d-flex align-items-center">
          <Icon icon="mdi:arrow-left" className="me-1" style={{ fontSize: '16px' }} /> 
          Back to Departments
        </button>
        <h5 className="card-title mb-0">{currentDepartment} - Districts</h5>
        <div></div>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table className="table vertical-striped-table mb-0" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th scope="col" style={{ width: '50px', textAlign: 'center' }}>Sr.No.</th>
                <th scope="col">District Name</th>
                <th scope="col">Active Users</th>
                <th scope="col">Total Activities</th>
                <th scope="col">Total Plants</th>
              </tr>
            </thead>
            <tbody>
              {drillDownData && drillDownData.length > 0 ? (
                drillDownData.map((district, index) => (
                  <tr key={index}>
                    <td style={{ width: '50px', textAlign: 'center' }}>{index + 1}</td>
                    <td>
                      <h6 
                        className="text-md mb-0 fw-normal cursor-pointer text-primary" 
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleDistrictClick(district)}
                      >
                        {district.district || 'Unknown District'}
                      </h6>
                    </td>
                    <td>{district.active_users}</td>
                    <td>{district.totalActivities}</td>
                    <td>{district.totalPlants.toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-20">
                    <div className="d-flex flex-column align-items-center">
                      <Icon icon="mdi:database-off" className="text-3xl text-muted mb-10" />
                      <p className="text-muted">No district data available</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderTehsilsTable = () => (
    <>
      <div className="card-header d-flex justify-content-between align-items-center">
        <button onClick={handleBackClick} className="btn btn-secondary btn-sm d-flex align-items-center">
          <Icon icon="mdi:arrow-left" className="me-1" style={{ fontSize: '16px' }} /> 
          Back to Districts
        </button>
        <h5 className="card-title mb-0">{currentDepartment} - {currentDistrict} - Tehsils</h5>
        <div></div>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table className="table vertical-striped-table mb-0" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th scope="col" style={{ width: '50px', textAlign: 'center' }}>Sr.No.</th>
                <th scope="col">Tehsil Name</th>
                <th scope="col">Active Users</th>
                <th scope="col">Total Activities</th>
                <th scope="col">Total Plants</th>
              </tr>
            </thead>
            <tbody>
              {drillDownData && drillDownData.length > 0 ? (
                drillDownData.map((tehsil, index) => (
                  <tr key={index}>
                    <td style={{ width: '50px', textAlign: 'center' }}>{index + 1}</td>
                    <td>
                      <h6
                        className="text-md mb-0 fw-normal cursor-pointer text-primary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleTehsilClick(tehsil)}
                      >
                        {tehsil.tehsil || 'Unknown Tehsil'}
                      </h6>
                    </td>
                    <td>{tehsil.active_users}</td>
                    <td>{tehsil.totalActivities}</td>
                    <td>{tehsil.totalPlants.toLocaleString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-20">
                    <div className="d-flex flex-column align-items-center">
                      <Icon icon="mdi:database-off" className="text-3xl text-muted mb-10" />
                      <p className="text-muted">No tehsil data available</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  return (
    <div className="card">
      {loading && (
        <div 
          className="position-absolute w-100 h-100 d-flex justify-content-center align-items-center" 
          style={{ background: 'rgba(255,255,255,0.7)', zIndex: 1000 }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      
      {currentView === 'departments' && renderDepartmentsTable()}
      {currentView === 'districts' && renderDistrictsTable()}
      {currentView === 'tehsils' && renderTehsilsTable()}
      {currentView === 'map' && (
        <TehsilMapView
          plantations={tehsilPlantations}
          onBack={handleBackClick}
        />
      )}
    </div>
  );
};

export default ReportDepartmentSummary;